/**
 * Render a module's start-dialog option fields, wire them, read them back.
 * The fields are declared by the client module (`GameClient.optionFields`);
 * the server validates the result with the module's `validateOptions`.
 *
 * - `advanced` fields go into a collapsed "Weitere Regeln" section.
 * - `showWhen` fields appear only while another field has a given value.
 * - `teams` is a small list editor: colour swatch, name, remove, add.
 */
import { esc } from '../dom';
import type { OptionField, TeamColorChoice } from '../game-client';

type TeamsField = Extract<OptionField, { kind: 'teams' }>;

function teamRow(f: TeamsField, team: { name: string; color: string }, index: number): string {
  const color = f.colors.find((c) => c.value === team.color);
  const key = esc(f.key);
  return `<li class="team-row" data-team-row>
    <button type="button" class="team-swatch" data-team-color style="--team: var(--team-${esc(team.color)})"
      aria-label="Farbe ${esc(color?.label ?? team.color)}, tippen zum Wechseln"></button>
    <input type="hidden" name="${key}.color" value="${esc(team.color)}" />
    <input class="team-row__name" name="${key}.name" value="${esc(team.name)}" maxlength="${String(f.maxNameLength)}"
      aria-label="Name von Team ${String(index + 1)}" autocomplete="off" />
    <button type="button" class="team-row__remove" data-team-remove aria-label="Team entfernen">×</button>
  </li>`;
}

function renderOne(f: OptionField): string {
  const help = f.help !== undefined ? `<span class="field__help">${esc(f.help)}</span>` : '';
  const name = esc(f.key);
  switch (f.kind) {
    case 'number':
      return `<label class="field">${esc(f.label)}<input name="${name}" type="number" inputmode="numeric" value="${String(f.default)}" min="${String(f.min)}" max="${String(f.max)}" step="${String(f.step ?? 1)}" />${help}</label>`;
    case 'boolean':
      return `<label class="field field--check"><input name="${name}" type="checkbox" ${f.default ? 'checked' : ''} /><span>${esc(f.label)}${help}</span></label>`;
    case 'select':
      return `<label class="field">${esc(f.label)}<select name="${name}">${f.choices
        .map(
          (c) =>
            `<option value="${esc(c.value)}" ${c.value === f.default ? 'selected' : ''}>${esc(c.label)}</option>`,
        )
        .join('')}</select>${help}</label>`;
    case 'text':
      return `<label class="field">${esc(f.label)}<input name="${name}" value="${esc(f.default)}" maxlength="${String(f.maxLength ?? 100)}" />${help}</label>`;
    case 'teams':
      return `<fieldset class="field teams-field" data-teams="${name}">
        <legend>${esc(f.label)}</legend>
        <ol class="teams-field__list" data-team-list>${f.default.map((t, i) => teamRow(f, t, i)).join('')}</ol>
        <button type="button" class="btn btn--ghost btn--small" data-team-add>+ Team</button>
        ${help}
      </fieldset>`;
  }
}

function wrap(f: OptionField): string {
  const html = renderOne(f);
  if (f.showWhen === undefined) return html;
  return `<div class="option-when" data-show-key="${esc(f.showWhen.key)}" data-show-equals="${esc(JSON.stringify([f.showWhen.equals].flat().map(String)))}">${html}</div>`;
}

/** All fields: basic ones first, advanced ones in a collapsed section. */
export function renderOptionFields(fields: OptionField[]): string {
  const basic = fields.filter((f) => f.advanced !== true);
  const advanced = fields.filter((f) => f.advanced === true);
  return `${basic.map(wrap).join('')}${
    advanced.length > 0
      ? `<details class="options-more"><summary>Weitere Regeln</summary><div class="options-more__body">${advanced.map(wrap).join('')}</div></details>`
      : ''
  }`;
}

/** Kept for single fields; prefer {@link renderOptionFields}. */
export function renderOptionField(f: OptionField): string {
  return wrap(f);
}

function currentValue(form: HTMLFormElement, key: string): string {
  const el = form.elements.namedItem(key);
  if (el instanceof HTMLInputElement && el.type === 'checkbox') return String(el.checked);
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) return el.value;
  return '';
}

/**
 * Make the fields interactive: conditional visibility and the team editor.
 * Call once after the fields are in the DOM.
 */
export function bindOptionFields(form: HTMLFormElement, fields: OptionField[]): void {
  const updateVisibility = (): void => {
    for (const el of form.querySelectorAll<HTMLElement>('[data-show-key]')) {
      const values = JSON.parse(el.dataset.showEquals ?? '[]') as string[];
      el.hidden = !values.includes(currentValue(form, el.dataset.showKey ?? ''));
    }
  };
  form.addEventListener('change', updateVisibility);
  updateVisibility();

  for (const f of fields) {
    if (f.kind !== 'teams') continue;
    const box = form.querySelector<HTMLElement>(`[data-teams="${CSS.escape(f.key)}"]`);
    if (box === null) continue;
    const list = box.querySelector<HTMLElement>('[data-team-list]');
    const add = box.querySelector<HTMLButtonElement>('[data-team-add]');
    if (list === null || add === null) continue;

    const rows = (): HTMLElement[] => [...list.querySelectorAll<HTMLElement>('[data-team-row]')];
    const colorOf = (row: HTMLElement): string =>
      row.querySelector<HTMLInputElement>(`input[name="${CSS.escape(f.key)}.color"]`)?.value ?? '';
    const used = (except?: HTMLElement): Set<string> =>
      new Set(
        rows()
          .filter((r) => r !== except)
          .map(colorOf),
      );
    const nextFree = (after: string, except?: HTMLElement): TeamColorChoice | undefined => {
      const taken = used(except);
      const start = f.colors.findIndex((c) => c.value === after);
      for (let i = 1; i <= f.colors.length; i += 1) {
        const c = f.colors[(start + i + f.colors.length) % f.colors.length];
        if (c !== undefined && !taken.has(c.value)) return c;
      }
      return undefined;
    };
    const refresh = (): void => {
      const n = rows().length;
      add.disabled = n >= f.max;
      for (const [i, r] of rows().entries()) {
        const remove = r.querySelector<HTMLButtonElement>('[data-team-remove]');
        if (remove !== null) remove.disabled = n <= f.min;
        r.querySelector('.team-row__name')?.setAttribute(
          'aria-label',
          `Name von Team ${String(i + 1)}`,
        );
      }
    };

    box.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const row = target.closest<HTMLElement>('[data-team-row]');
      if (target.closest('[data-team-color]') !== null && row !== null) {
        const old = colorOf(row);
        const next = nextFree(old, row);
        if (next === undefined) return;
        const oldChoice = f.colors.find((c) => c.value === old);
        const nameInput = row.querySelector<HTMLInputElement>('.team-row__name');
        // A name that was the colour's suggestion follows the colour.
        if (
          nameInput !== null &&
          (nameInput.value.trim() === '' || nameInput.value === oldChoice?.suggestedName)
        ) {
          nameInput.value = next.suggestedName;
        }
        row.outerHTML = teamRow(
          f,
          { name: nameInput?.value ?? next.suggestedName, color: next.value },
          rows().indexOf(row),
        );
        const fresh = rows().find((r) => colorOf(r) === next.value);
        fresh?.querySelector<HTMLElement>('[data-team-color]')?.focus();
        fresh?.querySelector('.team-swatch')?.classList.add('is-turning');
        refresh();
      } else if (target.closest('[data-team-remove]') !== null && row !== null) {
        if (rows().length <= f.min) return;
        row.classList.add('is-leaving');
        const done = (): void => {
          row.remove();
          refresh();
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
        else window.setTimeout(done, 280);
      } else if (target.closest('[data-team-add]') !== null) {
        if (rows().length >= f.max) return;
        const last = rows().at(-1);
        const next = nextFree(last === undefined ? '' : colorOf(last));
        if (next === undefined) return;
        list.insertAdjacentHTML(
          'beforeend',
          teamRow(f, { name: next.suggestedName, color: next.value }, rows().length),
        );
        const fresh = rows().at(-1);
        fresh?.classList.add('is-new');
        fresh?.querySelector<HTMLInputElement>('.team-row__name')?.select();
        refresh();
      }
    });
    refresh();
  }
}

export function readOptionValues(
  form: HTMLFormElement,
  fields: OptionField[],
): Record<string, unknown> {
  const data = new FormData(form);
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.kind === 'teams') {
      const names = data.getAll(`${f.key}.name`);
      const colors = data.getAll(`${f.key}.color`);
      out[f.key] = names.map((n, i) => ({
        name: typeof n === 'string' ? n : '',
        color: typeof colors[i] === 'string' ? colors[i] : '',
      }));
      continue;
    }
    const raw = data.get(f.key);
    const text = typeof raw === 'string' ? raw : '';
    out[f.key] = f.kind === 'number' ? Number(text) : f.kind === 'boolean' ? raw === 'on' : text;
  }
  return out;
}
